import {getCenteredPopupArgs} from './helpers/popups';
import {FinancialPartnerIntegrationError} from './FinancialPartnerIntegrationError';

export const DISABLED_STORAGE_MESSAGE =
  'Failed to read the {TYPE} property from Window: Access is denied for this document.';

const storageAccessHandler = {
  get(target: Record<string, string>, _prop: string) {
    throw new Error(DISABLED_STORAGE_MESSAGE.replace('{TYPE}', target.type));
  },
};

const disabledStorage = (type: string) => {
  return new Proxy({type}, storageAccessHandler);
};

let lastOpenedWindow: Window | null = null;
let lastOpenedWindowClosedPollTimer: number | null = null;

export const getLastOpenedWindow = (): Window | null => {
  return lastOpenedWindow;
};

/**
 * @returns true if there was a window to close.
 */
const clearWindowState = (logInfo: LogInfoFunction) => {
  const closedWindow = lastOpenedWindow !== null;
  lastOpenedWindow?.close();
  lastOpenedWindow = null;
  if (lastOpenedWindowClosedPollTimer !== null) {
    window.clearInterval(lastOpenedWindowClosedPollTimer);
    lastOpenedWindowClosedPollTimer = null;
  }
  logInfo(
    `Attempted to close the last opened window. closedWindow = ${closedWindow}`,
  );
  return closedWindow;
};

/**
 * @returns true if there was a window to close.
 */
export const closeLastOpenedWindow = (logInfo: LogInfoFunction) => {
  return clearWindowState(logInfo);
};

/**
 * If a popup was opened, focuses it.
 */
export const refocusLastOpenedWindow = () => {
  if (lastOpenedWindow) {
    lastOpenedWindow.focus();
  }
};

export const redirectLastOpenedWindow = (url: string) => {
  if (lastOpenedWindow) {
    lastOpenedWindow.location.href = url;
  }
};

const wrappedOpen = ({
  originalOpen,
  onCanceledCallback,
  logInfo,
}: {
  originalOpen: typeof window.open;
  onCanceledCallback: () => void;
  logInfo: LogInfoFunction;
}): typeof window.open => {
  return (...args: Parameters<typeof window.open>) => {
    if (lastOpenedWindow) {
      // The popup was already open! We don't allow 2 popups, so just navigate
      // to the target URL discarding any additional configuration of `target`
      // and `features` in the 2nd and 3rd positional arguments.
      return delegateWindowOpenToWindowLocation(lastOpenedWindow, ...args);
    }

    // No popup was already open. Clear existing state from previous opens and
    // open a new one.
    clearWindowState(logInfo);

    // Override the window positioning to always center the window
    const centeredPopupArgs = getCenteredPopupArgs(args);
    lastOpenedWindow = originalOpen(...centeredPopupArgs);

    // When the popup is closed, clear the state and call onCanceledCallback
    // so that stripe.js can inform the merchant.
    lastOpenedWindowClosedPollTimer = window.setInterval(function () {
      if (lastOpenedWindow?.closed) {
        // Note: This ordering matters, onCanceledCallback must be called before clearWindowState.
        onCanceledCallback();
        clearWindowState(logInfo);
      }
    }, 200);

    return lastOpenedWindow;
  };
};

const delegateWindowOpenToWindowLocation = (
  openWindow: Window,
  ...openArgs: Parameters<typeof window.open>
): ReturnType<typeof window.open> => {
  const [url] = openArgs;

  if (typeof url === 'string') {
    openWindow.location.href = url;
  } else if (url instanceof URL) {
    openWindow.location.href = url.toString();
  } else if (!url) {
    // Caller trying to send us to nowhere? We'll just do nothing.
  } else {
    throw new FinancialPartnerIntegrationError({
      code: 'window_open_failed',
      message:
        'Unexpected type for URL parameter in window.open. Expected a string and got some other type.',
    });
  }

  return lastOpenedWindow;
};

export type LogInfoFunction = (
  message: string,
  data?: Record<string, unknown>,
) => void;

const injectScript = (
  SCRIPT: string,
  onLoadCallback: () => unknown,
  onError: OnErrorEventHandler,
  onCanceledCallback: () => void,
  logInfo: LogInfoFunction,
) => {
  const script = document.createElement('script');
  script.src = SCRIPT;
  script.onload = onLoadCallback;
  script.onerror = onError;

  // Override localStorage, sessionStorage and indexedDb before loading the script
  Object.defineProperty(window, 'localStorage', {
    value: disabledStorage('localStorage'),
    writable: false,
    configurable: false,
  });
  Object.defineProperty(window, 'sessionStorage', {
    value: disabledStorage('sessionStorage'),
    writable: false,
    configurable: false,
  });
  Object.defineProperty(window, 'indexedDB', {
    value: disabledStorage('indexedDB'),
    writable: false,
    configurable: false,
  });

  // Wrap window.open to track window handles before loading the partner script
  Object.defineProperty(window, 'open', {
    value: wrappedOpen({
      originalOpen: window.open,
      onCanceledCallback,
      logInfo,
    }),
    writable: false,
    configurable: false,
  });
  addEventListener('beforeunload', () => {
    closeLastOpenedWindow(logInfo);
  });

  document.body && document.body.appendChild(script);
};

export default injectScript;
